import React, { useState } from 'react';
import { Box, Paper, Typography, useTheme, Grid, Card, CardContent, LinearProgress, Chip, alpha } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import { ResponsiveBar, ResponsivePie } from '@nivo/core';
import { ResponsiveBar as BarChart } from '@nivo/bar';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import { EmptyState } from '../../../../components/global';

const BeneficiaryAnalysis = () => {
  const theme = useTheme();
  const { data, error, isLoading } = useSWR('/reports/beneficiary-analysis', async () => {
    const response = await api.get('/reports/beneficiary-analysis');
    return response;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('el-GR').format(num || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Ανάλυση Συναλλασσομένων" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const beneficiaries = data || [];
  const topBeneficiaries = beneficiaries.slice(0, 10);

  const columns = [
    {
      field: 'companyName',
      headerName: 'Εταιρεία',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'vatNo',
      headerName: 'ΑΦΜ',
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: 'totalAmount',
      headerName: 'Συνολικό Ποσό',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'actualAmount',
      headerName: 'Πραγματικά',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'predictedAmount',
      headerName: 'Προβλέψεις',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'transactions',
      headerName: 'Συναλλαγές',
      flex: 0.8,
      minWidth: 120,
      align: 'center',
    },
    {
      field: 'averageTransaction',
      headerName: 'Μέσος Όρος',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'productionsCount',
      headerName: 'Παραγωγές',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
    },
  ];

  const chartData = topBeneficiaries.map((b, index) => ({
    beneficiary: b.companyName.length > 20 ? b.companyName.substring(0, 20) + '...' : b.companyName,
    amount: b.totalAmount,
  }));

  const handleExportToExcel = () => {
    // Implementation
  };

  const handleExportToPdf = () => {
    // Implementation
  };

  const handleExportToCsv = () => {
    // Implementation
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Ανάλυση Συναλλασσομένων" module="Αναφορές" subtitle="Ανάλυση εξόδων ανά συνάλλασσο" />

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <PeopleOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatNumber(beneficiaries.length)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Σύνολο Συναλλασσομένων
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                  }}
                >
                  <MonetizationOnOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatCurrency(beneficiaries.reduce((sum, b) => sum + b.totalAmount, 0))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Συνολικό Ποσό
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                  }}
                >
                  <MonetizationOnOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatCurrency(beneficiaries.reduce((sum, b) => sum + b.actualAmount, 0))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Πραγματικά Έξοδα
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.main,
                  }}
                >
                  <MonetizationOnOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatCurrency(beneficiaries.reduce((sum, b) => sum + b.predictedAmount, 0))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Προβλέψεις
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart */}
      {chartData.length > 0 && (
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            height: 400,
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Top 10 Συναλλασσόμενοι κατά Ποσό
          </Typography>
          <Box sx={{ height: 320 }}>
            <BarChart
              data={chartData}
              keys={['amount']}
              indexBy="beneficiary"
              margin={{ top: 20, right: 20, bottom: 80, left: 80 }}
              padding={0.3}
              valueFormat={formatCurrency}
              colors={theme.palette.primary.main}
              axisLeft={{
                format: (value) => formatCurrency(value),
              }}
              axisBottom={{
                tickRotation: -45,
              }}
              theme={{
                text: {
                  fill: theme.palette.text.primary,
                  fontSize: 12,
                },
                grid: {
                  line: {
                    stroke: theme.palette.divider,
                  },
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Data Grid */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: theme.shadows[3],
        }}
      >
        <FormListToolbar
          disableAdd
          handleExportToExcel={handleExportToExcel}
          handleExportToPdf={handleExportToPdf}
          handleExportToCsv={handleExportToCsv}
        />
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={beneficiaries.map((b, index) => ({ ...b, id: index }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default BeneficiaryAnalysis;

